import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Power, Store, CircleDot, Clock, CheckCircle2 } from "lucide-react";

const DEFAULT_HOURS = { enabled: false, open_time: "08:00", close_time: "23:00", days: null };
const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABELS = {
  fr: ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"],
  en: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
};

/**
 * Restaurant Open / Closed control for the owner.
 * - Manual toggle: PATCH /api/my/restaurant/open?open=true|false
 * - Scheduled hours: PATCH /api/my/restaurant/settings { hours: {...} }
 * When hours.enabled, the schedule drives the effective state (computed server-side).
 * Admin capabilities are untouched (admin can still suspend independently).
 */
export default function RestaurantOpenToggle() {
  const { t, i18n } = useTranslation();
  const i18nLang = i18n.language?.startsWith("en") ? "en" : "fr";
  const [state, setState] = useState({ loading: true, open: false, name: "", hours: DEFAULT_HOURS });
  const [busy, setBusy] = useState(false);
  const [savingHours, setSavingHours] = useState(false);
  const [draftHours, setDraftHours] = useState(DEFAULT_HOURS);

  const refresh = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/my/restaurant");
      if (!data) {
        setState({ loading: false, open: false, name: "", hours: DEFAULT_HOURS });
        return;
      }
      const hours = { ...DEFAULT_HOURS, ...(data.hours || {}) };
      setState({ loading: false, open: !!data.open, name: data.name || "", hours });
      setDraftHours(hours);
    } catch (err) {
      setState({ loading: false, open: false, name: "", hours: DEFAULT_HOURS });
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const toggle = async () => {
    const next = !state.open;
    setBusy(true);
    try {
      await apiClient.patch(`/my/restaurant/open?open=${next}`);
      setState((s) => ({ ...s, open: next }));
      toast.success(
        next
          ? t("rdash.toastOpen")
          : t("rdash.toastClosed")
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const saveHours = async (nextHours) => {
    setSavingHours(true);
    try {
      const { data } = await apiClient.patch("/my/restaurant/settings", { hours: nextHours });
      const hours = { ...DEFAULT_HOURS, ...(data?.hours || nextHours) };
      setDraftHours(hours);
      setState((s) => ({ ...s, hours, open: typeof data?.open === "boolean" ? data.open : s.open }));
      toast.success(
        nextHours.enabled
          ? t("rdash.toastHoursOn")
          : t("rdash.toastHoursOff")
      );
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingHours(false);
    }
  };

  if (state.loading) {
    return (
      <div className="bg-white border border-sand rounded-2xl p-4 flex items-center gap-3 text-mute" data-testid="restaurant-open-toggle-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> {t("rdash.loadingStatus")}
      </div>
    );
  }

  const isOpen = state.open;
  const scheduleOn = !!state.hours.enabled;

  return (
    <div className="space-y-3">
      {/* Status card */}
      <div
        className={`rounded-2xl p-5 border-2 flex items-center justify-between flex-wrap gap-4 transition-colors ${
          isOpen ? "bg-[#E7F6EE] border-brand-green/40" : "bg-[#FFF5F6] border-brand-red/30"
        }`}
        data-testid="restaurant-open-toggle"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span
            className={`w-11 h-11 rounded-full grid place-items-center shrink-0 ${
              isOpen ? "bg-brand-green text-white" : "bg-brand-red text-white"
            }`}
          >
            {isOpen ? <Store className="w-5 h-5" /> : <Power className="w-5 h-5" />}
          </span>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-mute">
              {t("rdash.statusLabel")}
            </div>
            <div
              className={`font-display text-xl font-semibold flex items-center gap-2 ${
                isOpen ? "text-brand-green" : "text-brand-red"
              }`}
              data-testid="restaurant-open-status"
            >
              <CircleDot className="w-4 h-4" />
              {isOpen ? t("rdash.openState") : t("rdash.closedState")}
            </div>
            {state.name && <div className="text-xs text-mute truncate mt-0.5">{state.name}</div>}
            {scheduleOn && (
              <div className="text-xs text-mute mt-1 inline-flex items-center gap-1">
                <Clock className="w-3 h-3" /> {t("rdash.controlledByPrefix")} — {state.hours.open_time} → {state.hours.close_time}
              </div>
            )}
          </div>
        </div>

        {scheduleOn ? (
          <div
            data-testid="restaurant-schedule-badge"
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold bg-white border border-sand text-mute"
          >
            <Clock className="w-3.5 h-3.5" /> {t("rdash.autoBadge")}
          </div>
        ) : (
          <button
            type="button"
            onClick={toggle}
            disabled={busy}
            data-testid="restaurant-open-toggle-btn"
            className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white disabled:opacity-60 shadow-sm transition-colors ${
              isOpen ? "bg-ink hover:bg-[#333]" : "bg-brand-green hover:bg-[#007A4A]"
            }`}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />}
            {isOpen ? t("rdash.closeBtn") : t("rdash.openBtn")}
          </button>
        )}
      </div>

      {/* Scheduled hours card */}
      <div className="bg-white border border-sand rounded-2xl p-5" data-testid="restaurant-hours-card">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
          <div>
            <h3 className="font-display text-lg font-semibold flex items-center gap-2">
              <Clock className="w-4 h-4 text-brand-red" /> {t("rdash.hoursTitle")}
            </h3>
            <p className="text-xs text-mute mt-0.5">
              {t("rdash.hoursSub")}
            </p>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={draftHours.enabled}
              onChange={(e) => setDraftHours({ ...draftHours, enabled: e.target.checked })}
              data-testid="hours-enable-checkbox"
              className="w-4 h-4"
            />
            <span className="text-sm font-semibold">{t("rdash.enable")}</span>
          </label>
        </div>

        <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${draftHours.enabled ? "" : "opacity-60"}`}>
          <label className="block">
            <span className="text-[11px] uppercase tracking-[0.22em] text-mute block mb-1">{t("rdash.opening")}</span>
            <input
              type="time"
              value={draftHours.open_time}
              onChange={(e) => setDraftHours({ ...draftHours, open_time: e.target.value })}
              disabled={!draftHours.enabled}
              data-testid="hours-open-time"
              className="w-full bg-white border border-sand rounded-xl px-4 py-3 outline-none focus:border-brand-red disabled:bg-elevated"
            />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-[0.22em] text-mute block mb-1">{t("rdash.closing")}</span>
            <input
              type="time"
              value={draftHours.close_time}
              onChange={(e) => setDraftHours({ ...draftHours, close_time: e.target.value })}
              disabled={!draftHours.enabled}
              data-testid="hours-close-time"
              className="w-full bg-white border border-sand rounded-xl px-4 py-3 outline-none focus:border-brand-red disabled:bg-elevated"
            />
          </label>
        </div>
        {draftHours.enabled && draftHours.open_time > draftHours.close_time && (
          <p className="text-xs text-brand-red mt-2">
            💡 {t("rdash.afterMidnight")} ({draftHours.open_time} → {draftHours.close_time})
          </p>
        )}

        {/* Horaires différents selon le jour de la semaine */}
        <div className="mt-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!draftHours.days}
              onChange={(e) => setDraftHours({ ...draftHours, days: e.target.checked ? Object.fromEntries(DAY_KEYS.map((k) => [k, { closed: false, open_time: draftHours.open_time, close_time: draftHours.close_time }])) : null })}
              disabled={!draftHours.enabled}
              data-testid="hours-perday-checkbox"
              className="w-4 h-4 shrink-0"
            />
            <span className="text-sm font-semibold">{t("rdash.perDayHours")}</span>
          </label>
          {!draftHours.enabled && (
            <p className="text-xs text-mute mt-1 ml-6" data-testid="hours-perday-hint">{t("rdash.perDayHint")}</p>
          )}
        </div>
        {draftHours.enabled && draftHours.days && (
          <div className="mt-3 space-y-2" data-testid="hours-perday-editor">
            {DAY_KEYS.map((k, i) => {
              const d = draftHours.days[k] || { closed: false, open_time: draftHours.open_time, close_time: draftHours.close_time };
              const setDay = (patch) => setDraftHours({ ...draftHours, days: { ...draftHours.days, [k]: { ...d, ...patch } } });
              return (
                <div key={k} className="flex items-center gap-2 flex-wrap">
                  <span className="w-20 text-sm font-semibold">{DAY_LABELS[i18nLang][i]}</span>
                  <label className="inline-flex items-center gap-1.5 text-xs text-mute cursor-pointer">
                    <input type="checkbox" checked={d.closed} onChange={(e) => setDay({ closed: e.target.checked })} data-testid={`hours-day-closed-${k}`} className="w-3.5 h-3.5" />
                    {t("rdash.dayClosed")}
                  </label>
                  {!d.closed && (
                    <>
                      <input type="time" value={d.open_time} onChange={(e) => setDay({ open_time: e.target.value })} data-testid={`hours-day-open-${k}`} className="bg-white border border-sand rounded-lg px-2 py-1.5 text-sm outline-none focus:border-brand-red" />
                      <span className="text-mute text-xs">→</span>
                      <input type="time" value={d.close_time} onChange={(e) => setDay({ close_time: e.target.value })} data-testid={`hours-day-close-${k}`} className="bg-white border border-sand rounded-lg px-2 py-1.5 text-sm outline-none focus:border-brand-red" />
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <button
          type="button"
          onClick={() => saveHours(draftHours)}
          disabled={savingHours}
          data-testid="hours-save-btn"
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-red hover:bg-[#C81F2D] disabled:opacity-60 text-white px-5 py-2.5 text-sm font-semibold"
        >
          {savingHours ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          {t("rdash.saveHours")}
        </button>
      </div>
    </div>
  );
}
